import React, { useCallback, useEffect, useState } from 'react';
import { Divider, Heading, Spacer, Text, VStack, Wrap } from '@chakra-ui/react';
import {
  getAcceptedRegulationsFormValidationSchema,
  getBookingParamsPersonalDetailsFormValidationSchema,
  getCountries,
  getPersonalDetailsFormBookingParams,
  getSalutations,
} from '../../services/Booking/BookingService';
import { Button } from '@chakra-ui/button';
import { BookingParams } from '../../types/BookingTypes';
import { useFormik } from 'formik';
import { getErrors } from '../../services/Joi/JoiValidationService';
import { FormikSelect } from '../common/Form/FormikSelect';
import { FormikInput } from '../common/Form/FormikInput';
import { SingleObject } from '../../types/ObjectTypes';
import { Flex } from '@chakra-ui/layout';
import { FormikTextarea } from '../common/Form/FormikTextarea';
import { FormikCheckbox } from '../common/Form/FormikCheckbox';
import { omit, pick } from 'ramda';

export interface BookingPersonalDetailsFormProps {
  object: SingleObject;
  bookingParams: BookingParams;
  handleBookingParamsChange: (bookingParams: BookingParams) => void;
  handleStepChange: (value: number) => void;
  currentStep: number;
}

export const BookingPersonalDetailsForm = (props: BookingPersonalDetailsFormProps): JSX.Element => {
  const { bookingParams, handleBookingParamsChange, handleStepChange, currentStep, object } = props;
  const salutations = getSalutations();
  const countries = getCountries();
  const [isBookingButtonEnabled, setIsBookingButtonEnabled] = useState(true);
  const [isOfferButtonEnabled, setIsOfferButtonEnabled] = useState(true);
  const [bookingButtonText, setBookingButtonText] = useState('Jetzt kostenpflichtig buchen');
  const [offerButtonText, setOfferButtonText] = useState('Angebot erstellen und zusenden');

  const formik = useFormik({
    validateOnChange: false,
    initialValues: bookingParams,
    onSubmit: () => {
      handleStepChange(currentStep + 1);
    },
    
    validate: (values: BookingParams) => {
      const personalDetailsFormValues = omit(
        ['acceptedTermsOfService', 'acceptedPrivacyPolicy'],
        values,
      );
      const acceptedRegulationsFormValues: any = pick(
        ['acceptedTermsOfService', 'acceptedPrivacyPolicy'],
        values,
      );

      const personalDetailsFormErrors = getErrors(
        getBookingParamsPersonalDetailsFormValidationSchema(),
        getPersonalDetailsFormBookingParams(personalDetailsFormValues),
      );

      const acceptedRegulationsFormErrors = getErrors(
        getAcceptedRegulationsFormValidationSchema(),
        acceptedRegulationsFormValues,
      );

      if (Object.keys(personalDetailsFormErrors).length === 0) {
        handleBookingParamsChange(personalDetailsFormValues);
      }

      return { ...personalDetailsFormErrors, ...acceptedRegulationsFormErrors };
    },
  });

  const setParamsToOffer = () => {
    formik.values.Type = 'OFFER';
  }
  const setParamsToBooking = () => {
    formik.values.Type = 'BOOKING';
  }

  const handleStepBack = useCallback(() => {
    handleStepChange(currentStep - 1);
  }, [currentStep]);

  useEffect(() => {
    if(object.baseData.OnlineBooking && object.baseData.OnlineOffer) {
      setIsBookingButtonEnabled(true);
      setIsOfferButtonEnabled(true);
    }
    if(object.baseData.OnlineBooking && !object.baseData.OnlineOffer) {
      setIsBookingButtonEnabled(true);
      setIsOfferButtonEnabled(false);
    }
    if(!object.baseData.OnlineBooking && object.baseData.OnlineOffer) {
      setIsBookingButtonEnabled(false);
      setIsOfferButtonEnabled(true);
    }
    if(isOfferButtonEnabled){
      if(object.baseData.OnlineOffer && !object.baseData.OnlineRequest){
        setOfferButtonText('Angebot erstellen und zusenden');
      }
      else if(object.baseData.OnlineOffer && object.baseData.OnlineRequest){
        setOfferButtonText('Anfrage erstellen und zusenden');
      }
    }

  }, [object]);
  return (
    <VStack
      p={4}
      borderRadius={{ sm: 0, md: 4, lg: 4, xl: 4 }}
      borderWidth={1}
      borderColor={'gray.300'}
      backgroundColor={'white'}
      minW="100%"
      gridGap={4}>
      <Text w={'100%'} textAlign={'left'} fontSize={'xl'} textStyle={'category.primary'}>
        Ihre Kontaktinformationen
      </Text>

      <VStack
        alignItems={'flex-start'}
        width={'100%'}
        gridGap={4}
        as={'form'}
        // @ts-ignore
        onSubmit={formik.handleSubmit}>
        <Divider />
        <VStack alignItems={'flex-start'} width={'100%'} gridGap={4}>
          <Heading size={'md'} fontWeight={'semibold'} flex={1}>
            Ihre Daten
          </Heading>

          <VStack width={'100%'} gridGap={4}>
            <Flex
              width={'100%'}
              gridGap={4}
              alignItems={'baseline'}
              flexDirection={{ base: 'column', md: 'row' }}>
              <Flex flex={1} w={{ base: '100%', md: 'unset' }}>
                <FormikSelect
                  label={'Anrede'}
                  required={true}
                  options={salutations}
                  name={'customer.Salutation'}
                  formik={formik}
                />
              </Flex>

              <Flex flex={1} w={{ base: '100%', md: 'unset' }}>
                <FormikInput
                  label={'Titel'}
                  name={'customer.Title'}
                  formik={formik}
                  type={'text'}
                />
              </Flex>
            </Flex>

            <Flex
              width={'100%'}
              alignItems={'baseline'}
              gridGap={4}
              flexDirection={{ base: 'column', md: 'row' }}>
              <Flex flex={1} w={{ base: '100%', md: 'unset' }}>
                <FormikInput
                  label={'Vorname'}
                  required={true}
                  name={'customer.Firstname'}
                  formik={formik}
                  type={'text'}
                />
              </Flex>

              <Flex flex={1} w={{ base: '100%', md: 'unset' }}>
                <FormikInput
                  label={'Nachname'}
                  required={true}
                  name={'customer.Lastname'}
                  formik={formik}
                  type={'text'}
                />
              </Flex>
            </Flex>

            <Flex
              width={'100%'}
              gridGap={4}
              alignItems={'baseline'}
              flexDirection={{ base: 'column', md: 'row' }}>
              <Flex flex={1} w={{ base: '100%', md: 'unset' }}>
                <FormikInput
                  label={'Telefon'}
                  required={true}
                  name={'customer.Phone1'}
                  formik={formik}
                  type={'text'}
                />
              </Flex>

              <Flex flex={1} w={{ base: '100%', md: 'unset' }}>
                <FormikInput
                  label={'Email'}
                  required={true}
                  name={'customer.Email'}
                  formik={formik}
                  type={'text'}
                />
              </Flex>
            </Flex>
          </VStack>
        </VStack>

        <Divider />

        <Flex
          alignItems={'flex-start'}
          width={'100%'}
          gridGap={4}
          flexDirection={{ base: 'column', md: 'row' }}>
          <Heading size={'md'} fontWeight={'semibold'} flex={1} mb={5}>
            Adresse
          </Heading>
          <Flex flexDirection={'column'} width={{ base: '100%', md: 320 }} gridGap={4}>
            <FormikInput
              label={'Straße'}
              required={true}
              name={'customer.Street'}
              formik={formik}
              type={'text'}
            />
            <FormikInput
              label={'PLZ'}
              required={true}
              name={'customer.Postal'}
              formik={formik}
              type={'text'}
            />
            <FormikInput
              label={'Stadt'}
              required={true}
              name={'customer.City'}
              formik={formik}
              type={'text'}
            />
            <FormikSelect
              label={'Land'}
              name={'customer.Country'}
              formik={formik}
              options={countries}
            />
          </Flex>
        </Flex>

        <Divider />

        <VStack alignItems={'flex-start'} width={'100%'}>
          <Heading size={'md'} fontWeight={'semibold'} flex={1}>
            Persönliche Nachricht
          </Heading>
          <Flex flex={1} width={'100%'}>
            <FormikTextarea label={'Kommentar'} name={'BookingComment'} formik={formik} />
          </Flex>
        </VStack>

        <VStack alignItems={'flex-start'} width={'100%'} gridGap={4}>
          <Heading size={'md'} fontWeight={'semibold'} flex={1}>
            Bestimmungen
          </Heading>

          <FormikCheckbox
            name={'acceptedTermsOfService'}
            formik={formik}
            label={'Allgemeine Geschäftsbedinungen'}
            text={
              <Text>
                Hiermit bestätige ich, das ich die Allgemeinen Geschäftsbedinungen zur Kenntnis
                genommen habe. {object.baseData.Commons.linkTerms && <a href={object.baseData.Commons.linkTerms} target="_blank"><u>AGB</u></a>}
              </Text>
            }
          />
          <FormikCheckbox
            name={'acceptedPrivacyPolicy'}
            formik={formik}
            label={'Datenschutzbestimmungen'}
            text={
              <Text>
                Hiermit stimme ich der Verarbeitung/Speicherung meiner persönlichen Daten zu. {object.baseData.Commons.linkLegalNotice && <a href={object.baseData.Commons.linkLegalNotice} target="_blank"><u>Datenschutzbestimmungen</u></a>}
              </Text>
            }
          />
        </VStack>

        <Divider />
        <Text fontSize={'sm'} color={'gray.500'}>
          * Pflichtangaben
        </Text>
        <Wrap width={'100%'}>
          <Button
            alignSelf={'flex-start'}
            variant={'ghost'}
            colorScheme={'blue'}
            onClick={handleStepBack}>
            Zurück
          </Button>
          <Spacer flex={1} />
          {
            isOfferButtonEnabled &&
            <Button alignSelf={'flex-end'} colorScheme={'blue'} type={'submit'} onClick={setParamsToOffer}>
              {offerButtonText}
            </Button>
          }
          {
            isBookingButtonEnabled &&
            <Button alignSelf={'flex-end'} colorScheme={'blue'} type={'submit'} onClick={setParamsToBooking}>
              {bookingButtonText}
            </Button>
          }
        </Wrap>
      </VStack>
    </VStack>
  );
};
